import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Organization } from 'src/app/@core/models/infrastructure/Organization.model';
import { Infrastructure } from 'src/app/@core/models/infrastructure/Infrastructure.model';
import { InfrastructureService } from 'src/app/@core/services/infrastructure/infrastructure.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { AppService } from 'src/app/@core/services/app/app.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { DarkModeService } from 'src/app/@core/services/dark-mode/dark-mode.service';

@Component({
  selector: 'app-org-name-step',
  templateUrl: './org-name-step.component.html',
  styleUrls: ['./org-name-step.component.scss'],
})
export class OrgNameStepComponent implements OnInit, OnDestroy {
  constructor(
    public infraService: InfrastructureService,
    public appService: AppService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    public darkModeService: DarkModeService
  ) {}

  @Input() organization: Organization;
  @Input() new = true;

  infrastructure: Infrastructure;
  subscription: Subscription;

  public edit = false;
  public globalAdmin = false;
  private oldJenkinsConfig:string = "";

  public jenkinsConfig:string;
  public jenkinsSizeConfig:string;
  public jenkinsConfigSplit:string[] = []
  public grafana:number;
  public latam:number;

  public orgStatus:string;

  ngOnChanges(){
    this.grafana = this.organization.grafana
  }

  ngOnInit() {
    this.triggerCheckOrganizationStatus()
    this.jenkinsConfigSplit = this.organization.jenkinsConfig.split('-')
    if(this.jenkinsConfigSplit.length > 1) {
      this.jenkinsConfig = this.jenkinsConfigSplit[0]
      this.jenkinsSizeConfig = this.jenkinsConfigSplit[1]
    }
    else {
      this.jenkinsConfig = this.jenkinsConfigSplit[0]
      this.jenkinsSizeConfig = ''
    }
    this.grafana = this.organization.grafana
    this.latam = this.organization.latam
    this.subscription = this.infraService.currentInfra.subscribe(infra => {
      this.infrastructure = infra
      this.setDefaultJenkinsConfig();
      if (this.infrastructure?.acrName) {
         this.globalAdmin = true;
      }
    });

    this.oldJenkinsConfig = this.organization.jenkinsConfig;

    if (this.infrastructure == null) {
      this.infraService.refreshGlobalStatus();
    }
    if (this.organization == null) {
      this.organization = new Organization();
    }

  }

  ngOnDestroy(): void {
      this.subscription.unsubscribe()
  }

  setDefaultJenkinsConfig() {
    if (this.new && this.infrastructure != null) {
      this.organization.jenkinsConfig = this.infrastructure.availableJenkinsConfigs[this.infrastructure.availableJenkinsConfigs.length -1]
    }
  }

  save(): boolean {
    if (!this.validateJenkinsConfigVersion()) return false;

    this.edit = false;
    const loading = this.appService.openLoading();

    let currentJenkinsConfig = (<HTMLInputElement>document.getElementById("jenkinsConfig")).textContent;
    let currentJenkinsSize = (<HTMLInputElement>document.getElementById("jenkinsSizeConfig")).textContent;
    this.organization.jenkinsConfig = currentJenkinsConfig + '-' + currentJenkinsSize

    this.appService.openDialog("Update Jenkins", "Are you sure to change the Jenkins Config Version?", () => {
      this.infraService.updateOrganizationJenkinsConfigVersion(this.organization).subscribe(res => {
        loading.close();
        if (res.error) {
          this.snackBar.open(res.message, null, { duration: 5000, panelClass: 'failed-snackbar'});
          this.edit = true;
        } else {
          this.snackBar.open(res.message, null, { duration: 5000, panelClass: 'success-snackbar'});
          this.edit = false;
        }
      });
    },
    () => {
      loading.close();
      this.organization.jenkinsConfig = this.oldJenkinsConfig;
    });
  }

  cancel(): void {
    this.edit = false;
    this.organization.jenkinsConfig = this.oldJenkinsConfig;
  }

  /**
   * Return true if the version is valid. Return false if it is invalid.
   */
  validateJenkinsConfigVersion(): boolean {

    let oldConfig = this.oldJenkinsConfig.split('-')
    let currentJenkinsConfig = (<HTMLInputElement>document.getElementById("jenkinsConfig")).textContent;
    let currentJenkinsSize = (<HTMLInputElement>document.getElementById("jenkinsSizeConfig")).textContent;

    let message = "";

    // Sort available jenkisn versions
    const allAvailableJenkinsConfigs = this.infrastructure.availableJenkinsConfigs.concat(this.infrastructure.availableUpdateJenkinsConfigs).sort();

    // Check the version is not empty
    if (!currentJenkinsConfig) {
      message = "The version cannot be empty."
    }

    // Check the jenkins size is correct
    if(!this.infrastructure.availableUpdateJenkinsSizesConfigs.includes(currentJenkinsSize)) {
      message = "The jenkins size is not valid."
    }

    // Check the version is the latest version
    else if (this.isLatestVersion(oldConfig[0], allAvailableJenkinsConfigs)) {
      // Check if the size has changed
      if(oldConfig[1] === currentJenkinsSize) {
        message = "The Jenkins Config Version is already the latest version."
      }
    }
    // Check the version is the same
    else if (currentJenkinsConfig === oldConfig[0]) {
      // Check if the size has changed
      if(oldConfig[1] === currentJenkinsSize) {
        message = "The Jenkins Config Version is the same."
      }
    }
    // Check the version is in the available list
    else if (this.infrastructure.availableUpdateJenkinsConfigs.lastIndexOf(currentJenkinsConfig) < 0) {
      message = "The Jenkins Config Version is invalid."
    }
    // Check the version is greater than current one
    else if (!this.isGreaterThan(currentJenkinsConfig, oldConfig[0])) {
      message = "The Jenkins Config Version must be greater than the current one."
    }

    if (message !== "") {
      this.snackBar.open(message, null, { duration: 5000, panelClass: 'failed-snackbar'});
      return false;
    }
    return true;

  }

  isGreaterThan(v1: string, v2: string): boolean {
    return v1.localeCompare(v2) > 0;
  }

  getLatestVersion(versions: string[]): string {
    let latestVersion:string = "v1";
    versions.forEach(v => {
      if (this.isGreaterThan(v, latestVersion)) {
				latestVersion = v;
			}
    });
		return latestVersion;
  }

  isLatestVersion(version: string, versions: string[]): boolean {
    return this.getLatestVersion(versions) === version;
  }

  dataStreamOrganizationStatus: any;
  timerOrganizationStatus: any;
  triggerCheckOrganizationStatus() {
    if(!this.new) {
      this.dataStreamOrganizationStatus = this.infraService.checkOrganizationStatus(this.organization.name, this.organization.shortName).subscribe(
        result => {
          this.orgStatus = result.status
          this.timerOrganizationStatus = setTimeout(() => { this.triggerCheckOrganizationStatus() }, 60000)
        }
      )
    }
  }

  getOrganizationStatus() {
    setTimeout(() => {
      if(!this.new) {
        this.infraService.checkOrganizationStatus(this.organization.name, this.organization.shortName).subscribe(
          result => {
            this.orgStatus = result.status
          }
        )
      }
    }, 6000)
  }

  scaleDownOrganization() {
    const ok: () => void = () => {
      let version = this.organization.jenkinsConfig.split('-')[0].split('.')[0]
      this.infraService.scaleDownOrganization(this.organization.name, this.organization.shortName, version).subscribe();
      this.getOrganizationStatus()
      const msg = this.translate.instant('ALERT.CONFIRM-MESSAGE-SCALE-DOWN-ORG');
      this.snackBar.open(msg, null, { duration: 7000 });
    }
    this.translate.get(['ALERT.SURE_SCALE_DOWN_ORG', 'ALERT.CONFIRM-MESSAGE-ORG-OFF']).subscribe(values => {
      this.appService.openDialog(values['ALERT.SURE_SCALE_DOWN_ORG'], values['ALERT.CONFIRM-MESSAGE-ORG-OFF'], ok, () => { })
    })
  }

  scaleUpOrganization() {
    const ok: () => void = () => {
      let version = this.organization.jenkinsConfig.split('-')[0].split('.')[0]
      this.infraService.scaleUpOrganization(this.organization.name, this.organization.shortName, version).subscribe();
      this.getOrganizationStatus()
      const msg = this.translate.instant('ALERT.CONFIRM-MESSAGE-SCALE-UP-ORG');
      this.snackBar.open(msg, null, { duration: 7000 });
    }
    this.translate.get(['ALERT.SURE_SCALE_UP_ORG', 'ALERT.CONFIRM-MESSAGE-ORG-ON']).subscribe(values => {
      this.appService.openDialog(values['ALERT.SURE_SCALE_UP_ORG'], values['ALERT.CONFIRM-MESSAGE-ORG-ON'], ok, () => { })
    })
  }

  getConfigValue(event: any){
    this.organization.grafana = this.grafana ? 1 : 0;
    this.organization.latam = this.latam ? 1 : 0;
  }

  changeModoEdit(): void {
    this.edit = true;
  }
}
